import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import sideimg from './assets/side-img.jpg';
import vin from './assets/vin.jpg';
import logo from './assets/logo.png';
import { buildApiUrl, API_ENDPOINTS } from './utils/baseurl';

export default function VinayagaGoldFinanceLogin({ onLoginSuccess }) {
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mobileError, setMobileError] = useState('');
  const navigate = useNavigate();

  // Mobile number validation function
  const validateMobileNumber = (number) => {
    const cleanNumber = number.replace(/\D/g, ''); // Remove non-digits
    if (cleanNumber.length === 0) {
      return 'Mobile number is required';
    }
    if (cleanNumber.length !== 10) {
      return 'Mobile number must be exactly 10 digits';
    }
    if (!/^[6-9]/.test(cleanNumber)) {
      return 'Mobile number must start with 6, 7, 8, or 9';
    }
    return '';
  };

  // Handle mobile number input with validation
  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit to 10 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 10);
    setMobileNumber(cleanValue);
    
    // Clear error when user starts typing
    if (mobileError) {
      setMobileError('');
    }
  };

  const handleSignIn = async () => {
    // Validate mobile number
    const mobileValidationError = validateMobileNumber(mobileNumber);
    if (mobileValidationError) {
      setMobileError(mobileValidationError);
      return;
    }

    if (!password) {
      alert('Please enter your password');
      return;
    }

    setIsLoading(true);
    try {
      // Build URL with query parameters for GET request
      const url = `${buildApiUrl(API_ENDPOINTS.LOGIN)}?mobileNumber=${encodeURIComponent(mobileNumber)}&password=${encodeURIComponent(password)}`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "accept": "*/*",
        },
      });

      const text = await response.text();
      let data;

      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Invalid JSON from server:", text);
        throw new Error("Server did not return valid JSON.");
      }

      //console.log("Server response:", data);

      if (response.ok && data?.token) {
        // Save access token to localStorage - token is directly in data.token
        localStorage.setItem('accessToken', data.token);
        
        // Save user data if available
        if (data.userName) {
          const userData = {
            name: data.userName, // Map userName to name for Header component
            userName: data.userName,
            applicationUserId: data.applicationUserId,
            isNewUser: data.isNewUser,
            verificationResultCode: data.verificationResultCode,
            userType: data.userType,
            role: data.userType === 'EU' ? 'End User' : 'Admin' // Map userType to role
          };
          localStorage.setItem('userData', JSON.stringify(userData));
          
          // Call the callback to update parent component state
          if (onLoginSuccess) {
            onLoginSuccess({
              name: userData.name,
              role: userData.role
            });
          }
        }
        
        //console.log("Login successful, token saved:", data.token);
        
        // Navigate to loans
        navigate('/loans');
      } else {
        throw new Error(data?.errorMessage || data?.message || "Login failed: Invalid credentials.");
      }
    } catch (err) {
      console.error("Login failed:", err.message);
      alert("Login failed: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col lg:flex-row">
      {/* Left side - Gold/coins image */}
      <img
        src={sideimg}
        alt="Gold/coins"
        className="hidden lg:block w-1/2 h-full object-cover"
      />

      {/* Right side - Login form */}
      <div className="flex-1 bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center p-4 md:p-8">
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-4 md:p-8 w-full max-w-md">
          <div className="text-center">
            <img src={vin} alt="Ganesha" className="w-24 h-24 md:w-40 md:h-40 mx-auto mb-2 md:mb-4" />
          </div>

          {/* Logo and Ganesha image */}
          <div className="flex items-center justify-center mb-4 md:mb-6 space-x-2 md:space-x-4">
            <img src={logo} alt="Logo" className="w-48 h-20 md:w-62 md:h-28 mx-auto mb-2 md:mb-4 rounded-xl shadow-md" />
          </div>

          {/* Login form */}
          <div className="space-y-4 md:space-y-6">
            <div>
              <label className="block text-indigo-900 text-sm font-medium mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={mobileNumber}
                onChange={handleMobileNumberChange}
                className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all duration-200 text-gray-700 placeholder-gray-400 text-sm md:text-base ${
                  mobileError ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
                maxLength="10"
              />
              {mobileError && (
                <p className="text-red-500 text-xs md:text-sm mt-1">{mobileError}</p>
              )}
              {mobileNumber && mobileNumber.length < 10 && !mobileError && (
                <p className="text-gray-500 text-xs md:text-sm mt-1">
                  {10 - mobileNumber.length} digits remaining
                </p>
              )}
            </div>

            <div>
              <label className="block text-indigo-900 text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-3 pr-10 md:pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all duration-200 text-gray-700 placeholder-gray-400 text-sm md:text-base"
                  disabled={isLoading}
                  onKeyPress={(e) => e.key === 'Enter' && handleSignIn()}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-2 md:pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 md:h-5 md:w-5" />
                  ) : (
                    <Eye className="h-4 w-4 md:h-5 md:w-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSignIn}
              disabled={isLoading || mobileNumber.length !== 10 || !password}
              className="w-full bg-gradient-to-r from-indigo-800 to-indigo-900 text-white py-2 md:py-3 px-4 rounded-lg font-semibold text-base md:text-lg hover:from-indigo-900 hover:to-purple-900 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />}
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
            <footer className="text-center text-indigo-900 text-xs md:text-sm font-bold">
              © 2025 Tieup Solutions Pvt. Ltd., All Rights Reserved.
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}